import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cancel } from '@/components/Icons/Cancel';
import { Close } from '@/components/Icons/Close';
import { Document } from '@/components/Icons/Document';
import { Drafts } from '@/components/Icons/Drafts';
import { MAX_ATTACHMENT_SIZE_MB } from '@/hooks/useAttachmentUploads';
import { AttachmentErrorKind } from '@/types/components';
import type { PotentialAttachment } from '@/types/attachment';
import { tv } from '@/utils/tv';

const chip = tv({
  base: 'flex shrink-0 items-center gap-3 rounded-2xl py-3 pr-3 pl-4',
  variants: {
    isLight: { true: 'bg-card', false: 'bg-on-primary-surface' },
    hasError: { true: 'bg-red-soft' },
  },
});

const progressTrack = tv({
  base: 'absolute inset-x-0 top-full mt-1 h-[3px] overflow-hidden rounded-[10px]',
  variants: {
    isLight: { true: 'bg-on-primary-surface', false: 'bg-card' },
  },
});

interface Props {
  attachment: PotentialAttachment;
  isLight?: boolean;
  onRemove: (key: string) => void;
}

export const PendingAttachmentChip: React.FC<Props> = ({
  attachment,
  isLight = false,
  onRemove,
}) => {
  const { t } = useTranslation();

  const errorLabels: Record<AttachmentErrorKind, string> = {
    [AttachmentErrorKind.Oversize]: t(
      'mail.composeModal.attachmentError.oversize',
      {
        maxSizeInMb: MAX_ATTACHMENT_SIZE_MB,
      },
    ),
    [AttachmentErrorKind.InvalidType]: t(
      'mail.composeModal.attachmentError.invalidType',
    ),
    [AttachmentErrorKind.UploadFailed]: t(
      'mail.composeModal.attachmentError.upload',
    ),
  };
  const errorLabel = attachment.errorKind
    ? errorLabels[attachment.errorKind]
    : null;

  return (
    <div className={chip({ isLight, hasError: !!errorLabel })}>
      <div className="flex items-center gap-2">
        {errorLabel && <Cancel className="size-4 shrink-0 text-destructive" />}
        {!errorLabel && isLight && (
          <Drafts className="size-4 shrink-0 text-muted-foreground" />
        )}
        {!errorLabel && !isLight && (
          <Document className="size-4 shrink-0 text-foreground" />
        )}
        <div className="relative">
          <span
            className={`text-subhead-14 whitespace-nowrap ${errorLabel ? 'text-destructive' : 'text-foreground'}`}
          >
            {errorLabel ?? attachment.name}
          </span>
          {attachment.isUploading && (
            <span className={progressTrack({ isLight })}>
              <span className="block h-full rounded-[10px] bg-primary animate-attachment-progress" />
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-label={attachment.name}
        onClick={() => onRemove(attachment.key)}
        className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-subtle-foreground"
      >
        <Close className="size-4" />
      </button>
    </div>
  );
};
